import React, { useEffect, useState, useMemo, useRef } from 'react';
import { apiCall } from '../utils/api';

interface Song {
  id: number;
  title: string;
  artist: string;
  genre: string;
  pdf_path: string;
  vocal_type?: string | null;
}

// Lead-vocal filter. Mirrors VALID_VOCAL_TYPES in server/src/routes/songs.ts.
const VOCAL_FILTERS = [
  { value: '', label: 'All' },
  { value: 'cowboy', label: 'Cowboys' },
  { value: 'cowgirl', label: 'Cowgirls' },
  { value: 'duet', label: 'Duets' },
];

interface Gig {
  id: number;
  name: string;
  started_at: string;
  status: 'upcoming' | 'active' | 'ended';
  scheduled_date: string | null;
  venue: string | null;
  queue_count?: string;
  video_enabled?: boolean;
}

const SongCataloguePage: React.FC = () => {
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [userName, setUserName] = useState('');
  const [requesterEmail, setRequesterEmail] = useState('');
  const [videoConsent, setVideoConsent] = useState(false);
  const [queueMessage, setQueueMessage] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [currentGig, setCurrentGig] = useState<Gig | null>(null);
  const [upcomingGigs, setUpcomingGigs] = useState<Gig[]>([]);
  const [selectedGigId, setSelectedGigId] = useState<number | null>(null);
  const [gigLoading, setGigLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [vocalFilter, setVocalFilter] = useState('');
  // Live state for the duplicate-pick check: who's queued each song, and what was played already tonight.
  const [currentQueue, setCurrentQueue] = useState<{ song_id: number; user_name: string; status: string }[]>([]);
  const [recentlyPlayed, setRecentlyPlayed] = useState<{ song_id: number; user_name: string; completed_at: string | null }[]>([]);

  // Only show songs that have both charts (instrument charts or a full-band chart) and lyrics
  const catalogueSongs = useMemo(() => {
    return songs.filter(
      (s: any) => (s.chart_count > 0 || s.has_full) && s.has_lyrics
    );
  }, [songs]);

  const filteredSongs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return catalogueSongs.filter(s => {
      if (vocalFilter && s.vocal_type !== vocalFilter) return false;
      if (!term) return true;
      return s.title.toLowerCase().includes(term) || s.artist.toLowerCase().includes(term);
    });
  }, [catalogueSongs, searchTerm, vocalFilter]);

  // Only offer a filter the catalogue can actually satisfy.
  const availableVocalFilters = useMemo(() => {
    const present = new Set(catalogueSongs.map(s => s.vocal_type).filter(Boolean));
    return VOCAL_FILTERS.filter(f => f.value === '' || present.has(f.value));
  }, [catalogueSongs]);

  // Group the visible songs by first letter so the A-Z strip has somewhere to jump to.
  // Anything not starting with a letter (e.g. "9 to 5") collects under '#'.
  const groupedSongs = useMemo(() => {
    const groups = new Map<string, Song[]>();
    const sorted = [...filteredSongs].sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
    );
    for (const song of sorted) {
      const first = song.title.trim().charAt(0).toUpperCase();
      const letter = /[A-Z]/.test(first) ? first : '#';
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter)!.push(song);
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => (a === '#' ? -1 : b === '#' ? 1 : a.localeCompare(b)))
      .map(([letter, songs]) => ({ letter, songs }));
  }, [filteredSongs]);

  // Auto-focus name input when a song is selected
  useEffect(() => {
    if (selectedSong && nameInputRef.current) {
      nameInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Small delay so scroll finishes before focus (avoids keyboard pushing layout)
      setTimeout(() => nameInputRef.current?.focus(), 300);
    }
  }, [selectedSong]);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch current gig + upcoming gigs
      try {
        const [gigRes, upcomingRes] = await Promise.all([
          apiCall('/api/songs/gigs/current'),
          apiCall('/api/songs/gigs/upcoming'),
        ]);
        if (gigRes.ok) {
          const gigData = await gigRes.json();
          setCurrentGig(gigData);
          if (gigData) setSelectedGigId(gigData.id);
        }
        if (upcomingRes.ok) {
          const upcomingData = await upcomingRes.json();
          setUpcomingGigs(upcomingData);
        }
      } catch (err) {
        // Silently fail
      } finally {
        setGigLoading(false);
      }

      // Fetch songs
      try {
        const res = await apiCall('/api/songs');
        if (!res.ok) throw new Error('Failed to fetch songs');
        const data = await res.json();
        setSongs(data);
      } catch (err: any) {
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // Poll for gig status every 10 seconds
    const interval = setInterval(async () => {
      try {
        const [gigRes, upcomingRes] = await Promise.all([
          apiCall('/api/songs/gigs/current'),
          apiCall('/api/songs/gigs/upcoming'),
        ]);
        if (gigRes.ok) {
          const gigData = await gigRes.json();
          setCurrentGig(gigData);
        }
        if (upcomingRes.ok) {
          const upcomingData = await upcomingRes.json();
          setUpcomingGigs(upcomingData);
        }
      } catch (err) {
        // Silently fail
      }
    }, 30000); // 30s — gig status rarely changes, reduces server load at scale

    return () => clearInterval(interval);
  }, []);

  // Auto-select gig when only one is available, or default to active gig
  useEffect(() => {
    if (currentGig && !selectedGigId) {
      setSelectedGigId(currentGig.id);
    } else if (!currentGig && upcomingGigs.length === 1 && !selectedGigId) {
      setSelectedGigId(upcomingGigs[0].id);
    }
  }, [currentGig, upcomingGigs, selectedGigId]);

  // Poll queue + played so we know what's been picked tonight and can warn submitters.
  useEffect(() => {
    const fetchPicks = async () => {
      try {
        const [qRes, pRes] = await Promise.all([
          apiCall('/api/songs/queue'),
          apiCall('/api/songs/played?limit=50'),
        ]);
        if (qRes.ok) setCurrentQueue(await qRes.json());
        if (pRes.ok) setRecentlyPlayed(await pRes.json());
      } catch {
        // Silently — duplicate warning is a soft enhancement, not load-bearing.
      }
    };
    fetchPicks();
    const id = setInterval(fetchPicks, 10000);
    return () => clearInterval(id);
  }, []);

  // Build maps from song_id → who picked it, scoped to the currently active gig.
  // Queue items are only ever for the active gig (other statuses get archived on
  // gig end). Played items are global so we filter by completed_at vs gig start.
  const pickStatusBySongId = useMemo(() => {
    const m = new Map<number, { kind: 'playing' | 'queued' | 'played'; userName: string }>();
    for (const q of currentQueue) {
      m.set(q.song_id, { kind: q.status === 'playing' ? 'playing' : 'queued', userName: q.user_name });
    }
    if (currentGig?.started_at) {
      const gigStart = new Date(currentGig.started_at).getTime();
      for (const p of recentlyPlayed) {
        if (!p.completed_at) continue;
        if (new Date(p.completed_at).getTime() < gigStart) continue;
        if (!m.has(p.song_id)) {
          m.set(p.song_id, { kind: 'played', userName: p.user_name });
        }
      }
    }
    return m;
  }, [currentQueue, recentlyPlayed, currentGig]);

  const handleAddToQueue = async () => {
    if (!selectedSong || !userName || !selectedGigId) return;
    setAdding(true);
    setQueueMessage(null);
    try {
      const body: Record<string, unknown> = {
        song_id: selectedSong.id,
        user_name: userName,
        gig_id: selectedGigId,
      };
      // Only forward the email when the performer actually opted in. A bare
      // email without consent would still be persisted server-side, which we
      // explicitly don't want (and we can't auto-enrol them in the newsletter).
      const trimmedEmail = requesterEmail.trim();
      if (videoConsent && trimmedEmail) {
        body.requester_email = trimmedEmail;
        body.video_consent = true;
      }
      const res = await apiCall('/api/songs/queue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to add to queue');
      }
      setQueueMessage('Added to queue!');
      setSelectedSong(null);
      setUserName('');
      setRequesterEmail('');
      setVideoConsent(false);
    } catch (err: any) {
      setQueueMessage(err.message || 'Error adding to queue');
    } finally {
      setAdding(false);
    }
  };

  // Determine if any gig is available for requests
  const availableGigs: Gig[] = [
    ...(currentGig ? [currentGig] : []),
    ...upcomingGigs,
  ];
  const canRequest = availableGigs.length > 0 && selectedGigId !== null;
  const selectedGig = availableGigs.find(g => g.id === selectedGigId) || null;
  const videoEnabledForSelectedGig = !!selectedGig?.video_enabled;
  const trimmedEmail = requesterEmail.trim();
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  // Only block submit when the user is actively trying to opt in. If consent
  // is unticked, the email field is ignored entirely — typos / autofill there
  // shouldn't prevent the queue add.
  const emailBlocksSubmit = videoConsent && (trimmedEmail.length === 0 || !emailLooksValid);
  const submitDisabled = !userName || adding || emailBlocksSubmit;

  return (
    <div style={{ padding: '0 20px 20px' }}>
      <h1 style={{
        fontFamily: "'Special Elite', 'Roboto Slab', serif",
        color: '#ffd700',
        textShadow: '0 0 20px rgba(255,215,0,0.3)',
        letterSpacing: '1px',
      }}>Song Catalogue</h1>

      {/* Gig Status Banner */}
      {!gigLoading && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '20px',
          borderRadius: '8px',
          backgroundColor: canRequest ? 'rgba(46,204,113,0.12)' : 'rgba(231,76,60,0.12)',
          border: `1px solid ${canRequest ? '#2ecc71' : '#e74c3c'}`,
          color: canRequest ? '#2ecc71' : '#e74c3c',
        }}>
          {currentGig ? (
            <div>
              <span><strong>{currentGig.name}</strong> is live! Select a song and add your name to join the queue.</span>
              {upcomingGigs.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '14px', color: '#c8a060' }}>
                  You can also request songs for upcoming gigs below.
                </div>
              )}
            </div>
          ) : upcomingGigs.length > 0 ? (
            <div style={{ color: '#c8a060' }}>
              <strong>No gig is live right now</strong>, but you can request songs for upcoming gigs!
            </div>
          ) : (
            <span><strong>No gigs scheduled.</strong> Please wait for the band to announce upcoming gigs.</span>
          )}
        </div>
      )}

      {/* Gig Selector */}
      {!gigLoading && availableGigs.length > 1 && (
        <div style={{
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}>
          <label style={{ color: '#a08060', fontWeight: 'bold', fontSize: '14px' }}>Request for:</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {availableGigs.map(gig => (
              <button
                key={gig.id}
                onClick={() => setSelectedGigId(gig.id)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: selectedGigId === gig.id ? '2px solid #c8850f' : '1px solid #5a3a1a',
                  background: selectedGigId === gig.id ? 'rgba(200,133,15,0.15)' : 'rgba(40,25,15,0.95)',
                  color: selectedGigId === gig.id ? '#ffd700' : '#a08060',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: selectedGigId === gig.id ? 'bold' : 'normal',
                }}
              >
                {gig.name}
                {gig.status === 'active' && <span style={{ color: '#2ecc71', marginLeft: '6px', fontSize: '11px' }}>LIVE</span>}
                {gig.status === 'upcoming' && gig.scheduled_date && (
                  <span style={{ color: '#806040', marginLeft: '6px', fontSize: '11px' }}>
                    {new Date(gig.scheduled_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}


      {loading && <p style={{ color: '#a08060' }}>Loading songs...</p>}
      {error && <p style={{ color: '#e74c3c' }}>{error}</p>}
      {queueMessage && <p style={{ color: queueMessage === 'Added to queue!' ? '#2ecc71' : '#e74c3c' }}>{queueMessage}</p>}

      {/* Add to Queue panel */}
      {selectedSong && (
        <div style={{
          marginBottom: 20,
          border: '1px solid #5a3a1a',
          borderRadius: '8px',
          padding: 16,
          backgroundColor: 'rgba(40,25,15,0.95)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          borderTop: 'none',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Glow line */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, transparent, #c8850f, transparent)',
          }} />
          <h2 style={{
            marginTop: 0,
            fontFamily: "'Special Elite', 'Roboto Slab', serif",
            color: '#ffd700',
            textShadow: '0 0 20px rgba(255,215,0,0.3)',
          }}>Add "{selectedSong.title}" to Queue</h2>
          {!canRequest ? (
            <p style={{ color: '#e74c3c' }}>Cannot add songs - no gig is available for requests.</p>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <label style={{ fontWeight: 'bold', color: '#f5deb3' }}>
                Your Name:
              </label>
              <input
                ref={nameInputRef}
                type="text"
                value={userName}
                onChange={e => setUserName(e.target.value)}
                disabled={adding}
                placeholder="Enter your name"
                style={{
                  flex: '1 1 200px',
                  padding: '8px',
                  backgroundColor: '#28190f',
                  border: '1px solid #5a3a1a',
                  borderRadius: '4px',
                  color: '#f5deb3',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
              <button
                onClick={handleAddToQueue}
                disabled={submitDisabled}
                style={{
                  padding: '8px 20px',
                  background: submitDisabled
                    ? '#3a2510'
                    : 'linear-gradient(180deg, #c8850f 0%, #8b5a0a 100%)',
                  color: submitDisabled ? '#806040' : '#fff',
                  cursor: submitDisabled ? 'not-allowed' : 'pointer',
                  border: '1px solid #5a3a1a',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                {adding ? 'Adding...' : 'Confirm'}
              </button>
              <button
                onClick={() => {
                  setSelectedSong(null);
                  setRequesterEmail('');
                  setVideoConsent(false);
                }}
                disabled={adding}
                style={{
                  padding: '8px 20px',
                  backgroundColor: 'transparent',
                  color: '#a08060',
                  cursor: adding ? 'not-allowed' : 'pointer',
                  border: '1px solid #5a3a1a',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              >
                Cancel
              </button>
            </div>
          )}

          {canRequest && videoEnabledForSelectedGig && (
            <div style={{
              marginTop: 14,
              paddingTop: 14,
              borderTop: '1px dashed #5a3a1a',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <label htmlFor="requester-email" style={{ fontWeight: 'bold', color: '#f5deb3' }}>
                  Get a video of your performance? (optional)
                </label>
                <input
                  id="requester-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={requesterEmail}
                  onChange={e => setRequesterEmail(e.target.value)}
                  disabled={adding}
                  placeholder="you@example.com"
                  style={{
                    flex: '1 1 220px',
                    padding: '8px',
                    backgroundColor: '#28190f',
                    border: `1px solid ${trimmedEmail && !emailLooksValid ? '#e74c3c' : '#5a3a1a'}`,
                    borderRadius: '4px',
                    color: '#f5deb3',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                />
              </div>
              {trimmedEmail.length > 0 && (
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  color: '#d4a574',
                  fontSize: '13px',
                  lineHeight: 1.4,
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={videoConsent}
                    onChange={e => setVideoConsent(e.target.checked)}
                    disabled={adding}
                    style={{ marginTop: 2, accentColor: '#c8850f' }}
                  />
                  <span>
                    Email me a video of my performance, and let me know when Rodeoke plays again — I consent to being recorded for this purpose.
                  </span>
                </label>
              )}
              {videoConsent && trimmedEmail.length > 0 && !emailLooksValid && (
                <p style={{ margin: 0, color: '#e74c3c', fontSize: '12px' }}>
                  Please enter a valid email address.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Search input */}
      {!loading && catalogueSongs.length > 0 && (
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'rgba(20,10,5,0.97)',
          padding: '12px 0',
          marginBottom: '16px',
        }}>
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by title or artist..."
            style={{
              width: '100%',
              padding: '10px 14px',
              fontSize: '16px',
              boxSizing: 'border-box',
              backgroundColor: '#28190f',
              border: '1px solid #5a3a1a',
              borderRadius: '6px',
              color: '#f5deb3',
              outline: 'none',
            }}
          />
          {availableVocalFilters.length > 1 && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
              {availableVocalFilters.map(f => {
                const active = vocalFilter === f.value;
                return (
                  <button
                    key={f.value || 'all'}
                    onClick={() => setVocalFilter(f.value)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '14px',
                      border: active ? '1px solid #c8850f' : '1px solid #5a3a1a',
                      background: active ? 'rgba(200,133,15,0.2)' : 'rgba(40,25,15,0.95)',
                      color: active ? '#ffd700' : '#a08060',
                      fontWeight: active ? 'bold' : 'normal',
                      fontSize: '13px',
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: '6px', fontSize: '14px', color: '#806040' }}>
            {searchTerm.trim() || vocalFilter
              ? `${filteredSongs.length} of ${catalogueSongs.length} songs`
              : `${catalogueSongs.length} songs`}
          </div>
        </div>
      )}

      {/* No results message */}
      {!loading && (searchTerm.trim() || vocalFilter) && filteredSongs.length === 0 && (
        <p style={{ color: '#806040', fontStyle: 'italic', textAlign: 'center', padding: '40px 0' }}>
          {searchTerm.trim() ? `No songs match "${searchTerm}"` : 'No songs in that category'}
        </p>
      )}

      {/* Song list, grouped A-Z. Compact rows: the whole row is the tap target,
          so there is no separate button competing with it for space. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {groupedSongs.map(group => (
            <div key={group.letter} id={`letter-${group.letter}`} style={{ scrollMarginTop: '96px' }}>
              <div style={{
                color: '#c8850f',
                fontWeight: 'bold',
                fontSize: '13px',
                letterSpacing: '1px',
                padding: '10px 4px 6px',
                borderBottom: '1px solid #3a2510',
                marginBottom: '4px',
              }}>
                {group.letter}
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '2px',
                marginBottom: '10px',
              }}>
                {group.songs.map(song => {
                  const pick = pickStatusBySongId.get(song.id);
                  const palette = !pick ? null
                    : pick.kind === 'playing'
                    ? { fg: '#ffd700', label: '🎤 Playing' }
                    : pick.kind === 'queued'
                    ? { fg: '#e74c3c', label: '🎤 Queued' }
                    : { fg: '#a08060', label: '✓ Played' };
                  return (
                    <button
                      key={song.id}
                      onClick={() => {
                        if (pick) {
                          const phrase = pick.kind === 'playing'
                            ? `${pick.userName} is performing "${song.title}" right now`
                            : pick.kind === 'queued'
                            ? `${pick.userName} already has "${song.title}" in the queue tonight`
                            : `${pick.userName} already performed "${song.title}" tonight`;
                          if (!window.confirm(`${phrase}.\n\nPick it anyway?`)) {
                            return;
                          }
                        }
                        setSelectedSong(song);
                      }}
                      disabled={adding || !canRequest}
                      title={!canRequest
                        ? 'No gigs available for requests'
                        : pick ? `${palette!.label} — ${pick.userName}` : `Add "${song.title}" to the queue`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        textAlign: 'left',
                        padding: '9px 10px',
                        background: 'rgba(40,25,15,0.95)',
                        border: '1px solid #3a2510',
                        borderRadius: '6px',
                        color: '#f5deb3',
                        cursor: !canRequest ? 'not-allowed' : 'pointer',
                        opacity: !canRequest ? 0.55 : 1,
                        fontFamily: 'inherit',
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{
                          display: 'block',
                          fontWeight: 'bold',
                          fontSize: '15px',
                          lineHeight: 1.25,
                          color: '#f5deb3',
                        }}>{song.title}</span>
                        <span style={{
                          display: 'block',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: '#a08060',
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>{song.artist.split(',').join(', ')}</span>
                      </span>
                      {palette && (
                        <span style={{
                          flexShrink: 0,
                          maxWidth: '42%',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: palette.fg,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {palette.label} · {pick!.userName}
                        </span>
                      )}
                      <span aria-hidden style={{ flexShrink: 0, color: '#c8850f', fontSize: '18px', lineHeight: 1 }}>+</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* A-Z jump strip - faster than scrolling 79 songs on a phone */}
        {groupedSongs.length > 1 && (
          <div style={{
            position: 'sticky',
            top: '96px',
            display: 'flex',
            flexDirection: 'column',
            gap: '1px',
            flexShrink: 0,
            paddingTop: '10px',
          }}>
            {groupedSongs.map(group => (
              <button
                key={group.letter}
                onClick={() => document.getElementById(`letter-${group.letter}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                aria-label={`Jump to ${group.letter}`}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#c8a060',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  lineHeight: 1,
                  padding: '3px 5px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {group.letter}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SongCataloguePage;
